<template>
  <b-modal
    id="linked-risk-evidence-modal"
    size="xl"
    dialog-class="lr-modal-dialog"
    title="Why is this risky?"
    hide-footer
    scrollable
    @show="onShow"
    @hidden="onHidden"
  >
    <template #modal-title>
      <div class="d-flex align-items-center">
        <h5 class="text-primary font-weight-normal mb-0 mr-3">
          Linked Risk — Evidence
        </h5>
        <span
          v-if="customer && customer.name"
          class="badge badge-light text-dark font-weight-normal"
        >
          {{ customer.name }}
        </span>
      </div>
    </template>

    <div v-if="loading" class="text-center py-5">
      <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
      <p class="text-muted mt-2 mb-0">Computing linked risk…</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <i class="fa fa-exclamation-triangle fa-2x text-warning"></i>
      <p class="text-muted mt-2 mb-0">{{ error }}</p>
    </div>

    <div v-else-if="evidence" class="lr-evidence">
      <!-- Summary chips -->
      <div class="d-flex flex-wrap mb-4">
        <span class="badge lr-chip mr-2 mb-1" :class="badgeClass(evidence.level)">
          {{ evidence.level }} link risk
        </span>
        <span class="badge lr-chip badge-light border mr-2 mb-1">
          Score {{ evidence.score }}/100
        </span>
        <span class="badge lr-chip badge-light border mr-2 mb-1">
          {{ evidence.receiverCount }} receiver(s)
        </span>
        <span class="badge lr-chip badge-light border mr-2 mb-1">
          {{ evidence.linkedRemitters }} linked remitter(s)
        </span>
        <span
          v-if="evidence.maxFanIn > 0"
          class="badge lr-chip badge-light border mb-1"
        >
          Peak fan-in: {{ evidence.maxFanIn }}
        </span>
      </div>

      <!-- Scoring breakdown -->
      <h6 class="lr-section-title">How the score was calculated</h6>
      <table class="table table-sm lr-score-table mb-4">
        <thead>
          <tr>
            <th class="lr-col-factor">Risk Factor</th>
            <th class="lr-col-evidence">Evidence</th>
            <th class="lr-col-weight">Weight</th>
            <th class="lr-col-explain">Explanation</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(f, i) in evidence.factors" :key="i">
            <td class="font-weight-bold">{{ f.label }}</td>
            <td>{{ f.evidence }}</td>
            <td>
              <span class="badge" :class="weightClass(f.weight)">{{ f.weight }}</span>
            </td>
            <td class="text-muted">{{ f.explanation }}</td>
          </tr>
        </tbody>
      </table>

      <!-- This remitter's own receivers (always shown, so the count is concrete) -->
      <h6 class="lr-section-title">Receivers added by this remitter</h6>
      <div
        v-if="evidence.receiverNames && evidence.receiverNames.length"
        class="lr-names mb-4"
      >
        <span
          v-for="(n, i) in evidence.receiverNames"
          :key="i"
          class="lr-name-chip"
          >{{ n }}</span
        >
      </div>
      <p v-else class="text-muted small mb-4">
        This remitter has no receivers.
      </p>

      <!-- Raw backing data, per shared instrument -->
      <h6 class="lr-section-title">Shared receivers &amp; linked remitters</h6>
      <p class="lr-section-help">
        Only receivers that are also linked to <strong>another</strong> remitter appear here —
        those are the entanglement points that drive the score. Receivers this remitter added
        that no other remitter is linked to (listed above) are not a concern and are omitted.
      </p>
      <div v-if="evidence.links && evidence.links.length" class="mb-3">
        <input
          v-model="remitterSearch"
          type="text"
          class="form-control form-control-sm lr-search"
          placeholder="Search remitter name, PayAngel ID or phone…"
        />
      </div>
      <div v-if="evidence.links && evidence.links.length">
        <div
          v-for="(link, i) in evidence.links"
          :key="i"
          class="lr-receiver-card"
        >
          <div class="lr-receiver-head">
            <span class="lr-receiver-name">
              <i class="fa fa-user-o mr-2 text-muted" aria-hidden="true"></i>
              {{ link.receiverName }}
            </span>
          </div>

          <!-- Instruments that resolve to the SAME set of remitters are merged into one
               block (their tags shown together) so an identical list is not repeated. -->
          <div
            v-for="(grp, j) in remitterGroups(link)"
            :key="j"
            class="lr-instrument"
          >
            <div class="lr-instrument-head">
              <span class="lr-instrument-chips">
                <span
                  v-for="(inst, k) in grp.instruments"
                  :key="k"
                  class="lr-chip-inline"
                >
                  <span class="badge mr-1" :class="instrumentBadge(inst.type)">
                    {{ instrumentLabel(inst.type) }}
                  </span>
                  <span class="lr-instrument-value">{{ instrumentValueText(inst) }}</span>
                </span>
              </span>
              <span class="badge badge-dark lr-count-badge">
                {{ grp.remitterCount }} unique remitter{{ grp.remitterCount === 1 ? "" : "s" }}
              </span>
            </div>
            <p v-if="grp.instruments.length > 1" class="lr-group-note">
              The same remitters below are linked via all of the above.
            </p>

            <div class="lr-remitter-scroll">
              <table class="table table-sm lr-remitter-table mb-0">
                <thead>
                  <tr>
                    <th class="lr-col-rname">Linked Remitter</th>
                    <th class="lr-col-paid">PayAngel ID</th>
                    <th class="lr-col-phone">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(r, k) in filteredRemitters(grp.sampleRemitters)"
                    :key="k"
                  >
                    <td>{{ r.name }}</td>
                    <td>{{ r.payangelId || "—" }}</td>
                    <td>{{ r.phone || "—" }}</td>
                  </tr>
                  <tr v-if="!filteredRemitters(grp.sampleRemitters).length">
                    <td colspan="3" class="text-muted text-center py-2">
                      No remitters match “{{ remitterSearch }}”.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              v-if="remitterSearch && filteredRemitters(grp.sampleRemitters).length"
              class="lr-more"
            >
              Showing {{ filteredRemitters(grp.sampleRemitters).length }} of
              {{ grp.sampleRemitters.length }} remitters sharing this instrument.
            </div>
          </div>
        </div>
      </div>
      <p v-else class="text-muted small">
        No other remitters are linked to this remitter’s receivers.
      </p>

      <p class="text-muted small mt-3 mb-0">
        Receiver identity comes from the beneficiary record; the customer table is
        used only to confirm a linked remitter. Only genuine remitters are counted.
        This score is informational — Monitor, Restrict, or Blacklist still requires
        an analyst reason.
      </p>
    </div>
  </b-modal>
</template>

<script>
import { GET_LINKED_RISK_EVIDENCE } from "@/store/actions.type.js";

const INSTRUMENT_LABELS = {
  bank: "Bank account",
  momo: "MOMO number",
  exact: "Same receiver",
  notify: "Notify contact",
};

const INSTRUMENT_BADGES = {
  bank: "lr-badge-bank",
  momo: "lr-badge-momo",
  exact: "lr-badge-exact",
  notify: "lr-badge-notify",
};

export default {
  name: "LinkedRiskEvidenceModal",
  props: {
    customer: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      evidence: null,
      remitterSearch: "",
    };
  },
  methods: {
    // Client-side search across a group's remitter list (name / PayAngel ID / phone).
    filteredRemitters(list) {
      const q = (this.remitterSearch || "").trim().toLowerCase();
      if (!q) return list || [];
      return (list || []).filter((r) =>
        [r.name, r.payangelId, r.phone].some((v) => (v || "").toLowerCase().includes(q))
      );
    },
    onShow() {
      this.$nextTick(() => this.loadEvidence());
    },
    async loadEvidence() {
      this.evidence = null;
      this.error = null;
      this.remitterSearch = "";
      if (!this.customer || !this.customer.id) {
        this.error = "No remitter selected.";
        return;
      }
      this.loading = true;
      const response = await this.$store.dispatch(
        GET_LINKED_RISK_EVIDENCE,
        this.customer.id
      );
      this.loading = false;
      if (response && response.status == 200 && response.data.details) {
        this.evidence = response.data.details;
      } else {
        this.error = "Unable to load the risk breakdown for this remitter.";
      }
    },
    onHidden() {
      this.evidence = null;
      this.error = null;
      this.loading = false;
      this.remitterSearch = "";
    },
    // Merge a receiver's instruments that resolve to the SAME set of linked remitters
    // into one block, so an identical remitter list is not repeated per instrument type.
    // Instruments with a different remitter set stay in their own block.
    remitterGroups(link) {
      const groups = [];
      const byKey = new Map();
      for (const inst of link.instruments || []) {
        // Skip the weak "notify contact" signal — it isn't scored and just duplicates the
        // strong same-receiver / shared-instrument lists, which confuses the reader.
        if (inst.type === "notify") continue;
        const sample = inst.sampleRemitters || [];
        const sig =
          inst.remitterCount +
          "|" +
          sample
            .map((r) => r.payangelId || r.phone || r.name || "")
            .slice()
            .sort()
            .join(",");
        let g = byKey.get(sig);
        if (!g) {
          g = {
            instruments: [],
            remitterCount: inst.remitterCount,
            sampleRemitters: sample,
          };
          byKey.set(sig, g);
          groups.push(g);
        }
        g.instruments.push({ type: inst.type, value: inst.value });
      }
      return groups;
    },
    // Plain-language value for a shared instrument (avoids jargon like "Same beneficiary record").
    instrumentValueText(inst) {
      if (inst.type === "exact") return "all send to this one receiver";
      return inst.value;
    },
    instrumentLabel(type) {
      return INSTRUMENT_LABELS[type] || type;
    },
    instrumentBadge(type) {
      return INSTRUMENT_BADGES[type] || "badge-secondary";
    },
    badgeClass(level) {
      if (level === "High") return "badge-danger";
      if (level === "Medium") return "badge-warning";
      return "badge-success";
    },
    weightClass(weight) {
      if (weight === "High") return "badge-danger";
      if (weight === "Medium") return "badge-warning";
      if (weight === "Low") return "badge-success";
      if (weight && weight.indexOf("+") === 0 && weight !== "+0") {
        return "badge-info";
      }
      return "badge-light border";
    },
  },
};
</script>

<style scoped>
.lr-evidence {
  font-size: 14px;
}
.lr-section-title {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 12px;
  font-weight: 700;
  color: #6b7782;
  margin-bottom: 10px;
}
.lr-section-help {
  font-size: 12px;
  color: #6b7782;
  margin: -4px 0 12px;
}
.lr-search {
  max-width: 340px;
}
.lr-chip {
  font-size: 12px;
  padding: 6px 10px;
}
.lr-names {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.lr-name-chip {
  background: #eef2f5;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
}

/* Fixed layout so the wide explanation column wraps instead of forcing a horizontal scrollbar. */
.lr-score-table {
  table-layout: fixed;
  width: 100%;
}
.lr-score-table th,
.lr-score-table td {
  vertical-align: top;
  word-break: break-word;
  white-space: normal;
}
.lr-col-factor {
  width: 20%;
}
.lr-col-evidence {
  width: 33%;
}
.lr-col-weight {
  width: 10%;
}
.lr-col-explain {
  width: 37%;
}

.lr-receiver-card {
  border: 1px solid #e3e9ee;
  border-radius: 6px;
  margin-bottom: 16px;
  overflow: hidden;
}
.lr-receiver-head {
  background: #f4f7f9;
  padding: 10px 14px;
  border-bottom: 1px solid #e3e9ee;
}
.lr-receiver-name {
  font-weight: 700;
  font-size: 15px;
}
.lr-instrument {
  padding: 12px 14px;
  border-top: 1px dashed #eef2f5;
}
.lr-instrument:first-of-type {
  border-top: 0;
}
.lr-instrument-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}
.lr-instrument-chips {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.lr-chip-inline {
  display: inline-flex;
  align-items: center;
}
.lr-group-note {
  font-size: 12px;
  color: #6b7782;
  font-style: italic;
  margin: -2px 0 8px;
}
/* High-contrast instrument badges (the theme's badge-primary was too light to read). */
.lr-badge-exact {
  background: #2b6cb0;
  color: #fff;
}
.lr-badge-bank {
  background: #c53030;
  color: #fff;
}
.lr-badge-momo {
  background: #b7791f;
  color: #fff;
}
.lr-badge-notify {
  background: #4a5568;
  color: #fff;
}
.lr-instrument-value {
  font-family: monospace;
  font-size: 14px;
  word-break: break-all;
}
.lr-count-badge {
  font-size: 12px;
}
/* Each instrument's remitter list scrolls inside a bounded panel with a sticky header. */
.lr-remitter-scroll {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #edf1f4;
  border-radius: 4px;
}
.lr-remitter-table {
  table-layout: fixed;
  width: 100%;
}
.lr-remitter-table th,
.lr-remitter-table td {
  word-break: break-word;
}
.lr-remitter-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f4f7f9;
  border-top: 0;
}
.lr-remitter-table tbody tr:nth-child(even) td {
  background: #fafbfc;
}
.lr-col-rname {
  width: 45%;
}
.lr-col-paid {
  width: 25%;
}
.lr-col-phone {
  width: 30%;
}
.lr-more {
  font-size: 12px;
  color: #6b7782;
  font-style: italic;
}
</style>

<!-- Not scoped: BootstrapVue teleports .modal-dialog to <body>, so a scoped rule can't reach it. -->
<style>
.lr-modal-dialog {
  max-width: 1280px;
  width: 94vw;
}
</style>
